import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminDashboardService, AdminStats } from '../../services/adminDashboardService';
import AdminHeader from '../../components/admin/AdminHeader';

const SuperuserDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await adminDashboardService.getStats();
                setStats(data);
            } catch (err) {
                console.error('Error fetching dashboard stats:', err);
                setError('No se pudieron cargar las estadísticas');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    return (
        <>
            <AdminHeader
                title="⚙️ Panel de Administración"
                showBackButton={false}
            />

            <div className="container fade-in">
                {loading ? (
                    <div className="flex justify-center p-xl">
                        <div className="spinner"></div>
                    </div>
                ) : error ? (
                    <div className="card bg-red-50 text-red-600 mb-lg p-md">
                        {error}
                    </div>
                ) : (
                    <>
                        {/* Stats Badges - Compact horizontal row */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.75rem',
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            backgroundColor: 'var(--surface)',
                            borderRadius: '0.75rem',
                            border: '1px solid var(--secondary)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                borderRadius: '2rem',
                                border: '1px solid var(--info)'
                            }}>
                                <span style={{ fontSize: '1rem' }}>📅</span>
                                <span style={{ fontWeight: '700', color: 'var(--info)', fontSize: '1.1rem' }}>{stats?.appointmentsToday || 0}</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Turnos Hoy</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'rgba(190, 242, 100, 0.1)',
                                borderRadius: '2rem',
                                border: '1px solid var(--accent)'
                            }}>
                                <span style={{ fontSize: '1rem' }}>💰</span>
                                <span style={{ fontWeight: '700', color: 'var(--accent)', fontSize: '1.1rem' }}>${stats?.monthlyRevenue.toLocaleString() || 0}</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Ingresos del Mes</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'var(--secondary)',
                                borderRadius: '2rem',
                                border: '1px solid var(--secondary-light)'
                            }}>
                                <span style={{ fontSize: '1rem' }}>✂️</span>
                                <span style={{ fontWeight: '700', color: 'var(--accent)', fontSize: '1.1rem' }}>{stats?.activeBarbers || 0}</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Barberos Activos</span>
                            </div>
                        </div>
                    </>
                )}

                {/* Management Section */}
                <h2 className="mb-md text-lg font-bold" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🛠️</span> Gestión Rápida
                </h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem'
                }}>
                    {/* Sucursales */}
                    <div
                        className="card hover:shadow-lg transition-all"
                        style={{ cursor: 'pointer', padding: '1rem' }}
                        onClick={() => navigate('/admin/branches')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                backgroundColor: '#3b82f620',
                                padding: '0.5rem',
                                borderRadius: '0.5rem'
                            }}>🏢</span>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Sucursales</h3>
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Gestionar ubicaciones y horarios</p>
                        <span style={{ color: 'var(--accent)', fontWeight: '600', fontSize: '0.8rem' }}>Administrar →</span>
                    </div>

                    {/* Barberos */}
                    <div
                        className="card hover:shadow-lg transition-all"
                        style={{ cursor: 'pointer', padding: '1rem' }}
                        onClick={() => navigate('/admin/barbers')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                backgroundColor: '#10b98120',
                                padding: '0.5rem',
                                borderRadius: '0.5rem'
                            }}>✂️</span>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Barberos</h3>
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Perfiles y disponibilidad</p>
                        <span style={{ color: 'var(--accent)', fontWeight: '600', fontSize: '0.8rem' }}>Gestionar →</span>
                    </div>

                    {/* Clientes */}
                    <div
                        className="card hover:shadow-lg transition-all"
                        style={{ cursor: 'pointer', padding: '1rem' }}
                        onClick={() => navigate('/admin/clients')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                backgroundColor: '#a855f720',
                                padding: '0.5rem',
                                borderRadius: '0.5rem'
                            }}>👥</span>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Clientes</h3>
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Directorio global y ABM</p>
                        <span style={{ color: 'var(--accent)', fontWeight: '600', fontSize: '0.8rem' }}>Gestionar →</span>
                    </div>

                    {/* Servicios */}
                    <div
                        className="card hover:shadow-lg transition-all"
                        style={{ cursor: 'pointer', padding: '1rem' }}
                        onClick={() => navigate('/admin/services')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                backgroundColor: '#f59e0b20',
                                padding: '0.5rem',
                                borderRadius: '0.5rem'
                            }}>🏷️</span>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Tipos de Servicio</h3>
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Catálogo, precios y duraciones</p>
                        <span style={{ color: 'var(--accent)', fontWeight: '600', fontSize: '0.8rem' }}>Gestionar →</span>
                    </div>

                    {/* Promociones */}
                    <div
                        className="card hover:shadow-lg transition-all"
                        style={{ cursor: 'pointer', padding: '1rem' }}
                        onClick={() => navigate('/admin/promotions')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                backgroundColor: '#ec489920',
                                padding: '0.5rem',
                                borderRadius: '0.5rem'
                            }}>🎁</span>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Promociones</h3>
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Paquetes de turnos para staff</p>
                        <span style={{ color: 'var(--accent)', fontWeight: '600', fontSize: '0.8rem' }}>Gestionar →</span>
                    </div>

                    {/* Reportes */}
                    <div
                        className="card hover:shadow-lg transition-all"
                        style={{ cursor: 'pointer', padding: '1rem' }}
                        onClick={() => navigate('/admin/reports')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                backgroundColor: '#8b5cf620',
                                padding: '0.5rem',
                                borderRadius: '0.5rem'
                            }}>📊</span>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Reportes</h3>
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Estadísticas e ingresos</p>
                        <span style={{ color: 'var(--accent)', fontWeight: '600', fontSize: '0.8rem' }}>Ver reportes →</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SuperuserDashboard;


